import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productApi, reviewApi } from '../api';
import { Product, Review } from '../types';
import { useAuthStore } from '../store/authStore';
import { useCartStore } from '../store/cartStore';
import Loading from '../components/common/Loading';
import { toImageUrl } from '../utils/image';
import { formatPrice } from '../utils/price';

type ReviewSort = 'id,desc' | 'rating,desc' | 'rating,asc';

const SORT_LABELS: Record<ReviewSort, string> = {
  'id,desc': 'Latest',
  'rating,desc': 'Highest rated',
  'rating,asc': 'Lowest rated',
};

const ProductDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { addItem } = useCartStore();

  const [product, setProduct] = useState<Product | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewPage, setReviewPage] = useState(0);
  const [reviewTotalPages, setReviewTotalPages] = useState(0);
  const [reviewTotalCount, setReviewTotalCount] = useState(0);
  const [reviewSort, setReviewSort] = useState<ReviewSort>('id,desc');
  const [avgRating, setAvgRating] = useState(0);
  const [selectedImg, setSelectedImg] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'detail' | 'review'>('detail');
  const [loading, setLoading] = useState(true);
  const [addingCart, setAddingCart] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);

  const applyReviewData = (data: any) => {
    const rv = data.reviews ?? {};
    setReviews(rv.content ?? []);
    setReviewTotalPages(rv.totalPages ?? 0);
    setReviewTotalCount(data.totalCount ?? 0);
    setReviewPage(rv.page ?? 0);
    setAvgRating(data.averageRating ?? 0);
  };

  const fetchReviews = (page = 0, sort: ReviewSort = reviewSort) => {
    if (!id) return;
    reviewApi.getByProduct(Number(id), page, sort).then(r => applyReviewData(r.data.data));
  };

  useEffect(() => {
    if (!id) return;
    Promise.all([
      productApi.getOne(Number(id)),
      reviewApi.getByProduct(Number(id), 0, 'id,desc'),
    ]).then(([p, r]) => {
      setProduct(p.data.data);
      applyReviewData(r.data.data);
    }).finally(() => setLoading(false));
  }, [id]);

  const handleSortChange = (sort: ReviewSort) => {
    setReviewSort(sort);
    fetchReviews(0, sort);
  };

  const handleDeleteReview = async (reviewId: number) => {
    if (!window.confirm(t('review.deleteConfirm'))) return;
    try {
      await reviewApi.delete(reviewId);
      alert(t('review.deleted'));
      fetchReviews(reviewPage);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed');
    }
  };

  const handleAddCart = async () => {
    if (!user) { alert(t('cart.loginRequired')); navigate('/login'); return; }
    if (hasVariants && !selectedVariantId) { alert(t('product.selectVariant')); return; }
    setAddingCart(true);
    try {
      await addItem(product!.id, quantity, selectedVariantId);
      alert(t('product.cartAdded'));
    } catch (err: any) {
      alert(err.response?.data?.message || t('product.cartAddFailed'));
    } finally {
      setAddingCart(false);
    }
  };

  const handleBuyNow = async () => {
    if (!user) { alert(t('cart.loginRequired')); navigate('/login'); return; }
    if (hasVariants && !selectedVariantId) { alert(t('product.selectVariant')); return; }
    try {
      await addItem(product!.id, quantity, selectedVariantId);
      navigate('/cart');
    } catch (err: any) {
      alert(err.response?.data?.message || t('product.cartAddFailed'));
    }
  };

  if (loading || !product) return <Loading />;

  const allImages = product.images ?? [];
  const galleryImages = [product.thumbnailUrl, ...allImages.map(i => i.url)];
  const variants = product.variants ?? [];
  const hasVariants = variants.length > 0;
  const selectedVariant = variants.find(v => v.id === selectedVariantId) ?? null;
  const stock = hasVariants ? (selectedVariant?.stock ?? 0) : (product.baseStock ?? 0);
  const needVariant = hasVariants && !selectedVariant;
  const mainSrc = selectedVariant?.imageUrl
    ? toImageUrl(selectedVariant.imageUrl)
    : toImageUrl(galleryImages[selectedImg]);
  const discountRate = product.discountPercent ?? 0;

  return (
    <div style={styles.page}>
      <div style={styles.topSection}>
        <div style={styles.imageSection}>
          <img
            src={mainSrc}
            alt={product.name}
            style={styles.mainImage}
          />
          {galleryImages.length > 1 && (
            <div style={styles.thumbRow}>
              {galleryImages.map((url, i) => (
                <img
                  key={i}
                  src={toImageUrl(url)}
                  alt=""
                  style={{ ...styles.thumb, ...(selectedImg === i ? styles.thumbActive : {}) }}
                  onClick={() => setSelectedImg(i)}
                />
              ))}
            </div>
          )}
        </div>

        <div style={styles.infoSection}>
          {product.brandName && <p style={styles.brand}>{product.brandName}</p>}
          <h1 style={styles.name}>{product.name}</h1>

          <div style={styles.ratingRow}>
            {reviewTotalCount > 0 && (
              <>
                <span>⭐ {avgRating.toFixed(1)}</span>
                <span style={styles.reviewCount}>{t('product.reviewCount', { count: reviewTotalCount })}</span>
              </>
            )}
          </div>

          <div style={styles.priceSection}>
            {discountRate > 0 && (
              <>
                <span style={styles.discount}>{discountRate}{t('product.discount')}</span>
                <span style={styles.originalPrice}>{formatPrice(product.originalPrice, 'USD')}</span>
              </>
            )}
            <p style={styles.price}>{formatPrice(product.price, 'USD')}</p>
            {product.shippingFee != null && product.shippingFee > 0 && (
              <p style={styles.shipping}>+ {formatPrice(product.shippingFee, 'USD')} shipping</p>
            )}
          </div>

          <div style={styles.divider} />

          {hasVariants && (
            <div style={styles.variantRow}>
              <span style={styles.label}>{t('product.variant')}</span>
              <div style={styles.variantChips}>
                {variants.map(v => {
                  const soldOut = !v.available || v.stock <= 0;
                  const active = selectedVariantId === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      disabled={soldOut}
                      onClick={() => { setSelectedVariantId(v.id); setQuantity(1); }}
                      style={{
                        ...styles.variantChip,
                        ...(active ? styles.variantChipActive : {}),
                        ...(soldOut ? styles.variantChipDisabled : {}),
                      }}
                    >
                      {v.imageUrl && <img src={toImageUrl(v.imageUrl)} alt="" style={styles.variantChipImg} />}
                      <span>{v.name}</span>
                      {soldOut && <span style={styles.variantSoldOut}>{t('product.outOfStock')}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(user?.role === 'ADMIN' || needVariant || stock <= 0) && (
            <div style={styles.stockRow}>
              <span style={styles.label}>{t('product.stock')}</span>
              {needVariant ? (
                <span style={styles.outStock}>{t('product.selectVariant')}</span>
              ) : (
                <span style={stock > 0 ? styles.inStock : styles.outStock}>
                  {stock > 0
                    ? `${stock}${t('product.inStock')}`
                    : t('product.outOfStock')}
                </span>
              )}
            </div>
          )}

          <div style={styles.qtyRow}>
            <span style={styles.label}>{t('product.quantity')}</span>
            <div style={styles.qtyControl}>
              <button style={styles.qtyBtn} onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
              <span style={styles.qtyValue}>{quantity}</span>
              <button
                style={styles.qtyBtn}
                onClick={() => setQuantity(q => Math.min(stock, q + 1))}
                disabled={stock === 0}
              >+</button>
            </div>
          </div>

          <div style={styles.totalRow}>
            <span style={styles.label}>{t('product.total')}</span>
            <span style={styles.totalPrice}>{formatPrice(product.price * quantity, 'USD')}</span>
          </div>

          <div style={styles.buttonRow}>
            <button
              style={styles.cartBtn}
              onClick={handleAddCart}
              disabled={stock === 0 || addingCart || needVariant}
            >
              {addingCart ? t('product.addingCart') : t('product.addToCart')}
            </button>
            <button style={styles.buyBtn} onClick={handleBuyNow} disabled={stock === 0 || needVariant}>
              {t('product.buyNow')}
            </button>
          </div>
        </div>
      </div>

      <div style={styles.tabSection}>
        <div style={styles.tabs}>
          {(['detail', 'review'] as const).map(tab => (
            <button
              key={tab}
              style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'detail' ? t('product.detail') : `${t('product.reviews')} (${reviewTotalCount})`}
            </button>
          ))}
        </div>

        {activeTab === 'detail' && (
          <div style={styles.tabContent}>
            {product.description && (
              <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, marginBottom: 24 }}>{product.description}</p>
            )}
            {allImages.length > 0 ? (
              <div style={styles.detailImages}>
                {allImages.map((img, i) => (
                  <img key={i} src={toImageUrl(img.url)} alt={`detail-${i + 1}`} style={styles.detailImg} />
                ))}
              </div>
            ) : !product.description && (
              <p>{t('product.noDescription')}</p>
            )}
          </div>
        )}

        {activeTab === 'review' && (
          <div style={styles.tabContent}>
            <div style={styles.reviewListHeader}>
              <span>{t('product.reviews')}</span>
              <span style={styles.reviewListCount}>{reviewTotalCount}</span>
              <div style={styles.sortToggle}>
                {(Object.keys(SORT_LABELS) as ReviewSort[]).map(sort => (
                  <button
                    key={sort}
                    type="button"
                    style={{ ...styles.sortBtn, ...(reviewSort === sort ? styles.sortBtnActive : {}) }}
                    onClick={() => handleSortChange(sort)}
                  >{SORT_LABELS[sort]}</button>
                ))}
              </div>
            </div>

            {reviews.length === 0 ? (
              <p style={styles.noReview}>{t('product.noReviews')}</p>
            ) : (
              <>
                {reviews.map(review => (
                  <div key={review.id} style={styles.reviewItem}>
                    <div style={styles.reviewHeader}>
                      <span style={styles.reviewUser}>{review.userName}</span>
                      <span>{'⭐'.repeat(review.rating)}</span>
                      <span style={styles.reviewDate}>{review.createdAt.slice(0, 10)}</span>
                      {user && user.id === review.userId && (
                        <button type="button" style={styles.reviewDeleteBtn} onClick={() => handleDeleteReview(review.id)}>
                          {t('common.delete')}
                        </button>
                      )}
                    </div>
                    <p style={styles.reviewContent}>{review.content}</p>
                  </div>
                ))}
                {reviewTotalPages > 1 && (
                  <div style={styles.reviewPagination}>
                    {Array.from({ length: reviewTotalPages }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        style={{ ...styles.reviewPageBtn, ...(reviewPage === i ? styles.reviewPageBtnActive : {}) }}
                        onClick={() => fetchReviews(i)}
                      >{i + 1}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1100, margin: '40px auto', padding: '0 20px' },
  topSection: { display: 'flex', gap: 48, marginBottom: 60 },
  imageSection: { flex: '0 0 480px' },
  mainImage: { width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 12, background: '#f5f5f5' },
  thumbRow: { display: 'flex', gap: 8, marginTop: 12 },
  thumb: { width: 64, height: 64, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: '2px solid #eee' },
  thumbActive: { borderColor: '#b39ddb' },
  infoSection: { flex: 1 },
  brand: { fontSize: 13, color: '#999', marginBottom: 6 },
  name: { fontSize: 24, fontWeight: 700, color: '#333', marginBottom: 12, lineHeight: 1.4 },
  ratingRow: { display: 'flex', gap: 16, fontSize: 13, color: '#888', marginBottom: 20 },
  reviewCount: {},
  priceSection: { marginBottom: 20 },
  discount: { fontSize: 14, color: '#b39ddb', fontWeight: 700, marginRight: 8 },
  originalPrice: { fontSize: 14, color: '#bbb', textDecoration: 'line-through' },
  price: { fontSize: 28, fontWeight: 700, color: '#333', marginTop: 4 },
  shipping: { fontSize: 13, color: '#888', marginTop: 4 },
  divider: { height: 1, background: '#f0f0f0', margin: '20px 0' },
  variantRow: { display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 },
  variantChips: { display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1 },
  variantChip: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', border: '2px solid #e8e8e8', borderRadius: 8, background: '#fff', fontSize: 13, color: '#444', cursor: 'pointer' },
  variantChipActive: { borderColor: '#b39ddb', color: '#7e57c2', fontWeight: 700, background: '#f6f2fc' },
  variantChipDisabled: { opacity: 0.45, textDecoration: 'line-through', cursor: 'not-allowed' },
  variantChipImg: { width: 28, height: 28, objectFit: 'cover', borderRadius: 4 },
  variantSoldOut: { fontSize: 10, color: '#e53935' },
  stockRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 },
  qtyRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 },
  label: { width: 48, fontSize: 13, color: '#888' },
  inStock: { fontSize: 13, color: '#4caf50', fontWeight: 500 },
  outStock: { fontSize: 13, color: '#e53935', fontWeight: 500 },
  qtyControl: { display: 'flex', alignItems: 'center', border: '1px solid #e8e8e8', borderRadius: 6 },
  qtyBtn: { width: 36, height: 36, background: 'none', border: 'none', fontSize: 18, color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  qtyValue: { width: 48, textAlign: 'center', fontSize: 15, fontWeight: 600 },
  totalRow: { display: 'flex', alignItems: 'center', gap: 16, margin: '20px 0' },
  totalPrice: { fontSize: 24, fontWeight: 700, color: '#b39ddb' },
  buttonRow: { display: 'flex', gap: 12 },
  cartBtn: { flex: 1, padding: '16px', border: '2px solid #b39ddb', borderRadius: 8, background: '#fff', color: '#b39ddb', fontSize: 15, fontWeight: 600 },
  buyBtn: { flex: 1, padding: '16px', background: '#b39ddb', borderRadius: 8, border: 'none', color: '#fff', fontSize: 15, fontWeight: 600 },
  tabSection: { borderTop: '2px solid #333' },
  tabs: { display: 'flex' },
  tab: { flex: 1, padding: '16px', background: 'none', border: 'none', fontSize: 14, fontWeight: 500, color: '#888', cursor: 'pointer', borderBottom: '2px solid transparent' },
  tabActive: { color: '#333', fontWeight: 700, borderBottom: '2px solid #333' },
  tabContent: { padding: '32px 0', minHeight: 200 },
  detailImages: { display: 'flex', flexDirection: 'column', gap: 4 },
  detailImg: { width: '100%', height: 'auto', display: 'block' },
  noReview: { color: '#bbb', textAlign: 'center', padding: '40px 0' },
  reviewItem: { padding: '20px 0', borderBottom: '1px solid #f0f0f0' },
  reviewHeader: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 },
  reviewUser: { fontWeight: 600, fontSize: 14 },
  reviewDate: { fontSize: 12, color: '#bbb', marginLeft: 'auto' },
  reviewContent: { fontSize: 14, color: '#555', lineHeight: 1.6 },
  reviewListHeader: { display: 'flex', alignItems: 'center', gap: 8, padding: '0 0 16px', borderBottom: '1px solid #f0f0f0', fontSize: 14, fontWeight: 700, color: '#333' },
  reviewListCount: { color: '#b39ddb' },
  sortToggle: { marginLeft: 'auto', display: 'flex', gap: 6 },
  sortBtn: { padding: '6px 12px', border: '1px solid #e8e8e8', background: '#fff', borderRadius: 16, fontSize: 12, color: '#666', cursor: 'pointer', fontWeight: 500 },
  sortBtnActive: { background: '#b39ddb', color: '#fff', borderColor: '#b39ddb' },
  reviewDeleteBtn: { padding: '4px 10px', background: 'none', border: '1px solid #e8e8e8', borderRadius: 6, fontSize: 11, color: '#888', cursor: 'pointer' },
  reviewPagination: { display: 'flex', justifyContent: 'center', gap: 6, marginTop: 24 },
  reviewPageBtn: { width: 32, height: 32, border: '1px solid #e8e8e8', background: '#fff', borderRadius: 6, fontSize: 13, color: '#555', cursor: 'pointer' },
  reviewPageBtnActive: { background: '#b39ddb', color: '#fff', border: 'none' },
};

export default ProductDetailPage;
